import { useCallback, useEffect, useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';

// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';

const useDubbingNudge = ({ getDubbedSubtitleKeys }: { getDubbedSubtitleKeys: () => string[] }) => {
  const [shouldShowNudge, setShouldShowNudge] = useState(false);
  const [nudgeLanguageCode, setNudgeLanguageCode] = useState<string | null>(null);

  const userLanguageCode = getUserLanguageCode();
  const { setShouldNudgeBeVisible, shouldNudgeBeVisible, isDubbingAvailable } = useDubbingContext();

  const getMatches = (keys: string[], prefix: string) => {
    let prefixMatched = false;
    let languageMatched;

    keys.forEach((key: string) => {
      if (prefix === key) {
        prefixMatched = true;
        languageMatched = key;
      }
    });

    return { prefixMatched, languageMatched };
  };

  const shouldShowNudgeOnMatchingUserLanguage = useCallback(() => {
    // user switches to language code that is available in dubbed audio
    const dubbedSubtitleKeys = getDubbedSubtitleKeys();
    const prefix = userLanguageCode.split('-')[0];
    const { prefixMatched, languageMatched } = getMatches(dubbedSubtitleKeys, prefix);
    const showBanner = dubbedSubtitleKeys.includes(userLanguageCode) || prefixMatched;
    if (showBanner) {
      setShouldShowNudge(showBanner);
      setNudgeLanguageCode(prefixMatched && languageMatched ? languageMatched : userLanguageCode);
    }
  }, [getDubbedSubtitleKeys, userLanguageCode]);

  const shouldShowNudgeOnSubtitleChange = useCallback(
    (languageCode: string) => {
      const dubbedSubtitleKeys = getDubbedSubtitleKeys();
      const showBanner = languageCode && dubbedSubtitleKeys.includes(languageCode);
      if (showBanner) {
        setShouldShowNudge(showBanner);
        setNudgeLanguageCode(languageCode);
      } else {
        setShouldShowNudge(false);
        setNudgeLanguageCode(null);
        shouldShowNudgeOnMatchingUserLanguage();
        // trigger checks for nudge on transcript
        setShouldNudgeBeVisible({
          ...shouldNudgeBeVisible,
          isVisible: true,
        });
      }
    },
    [getDubbedSubtitleKeys, setShouldNudgeBeVisible, shouldNudgeBeVisible, shouldShowNudgeOnMatchingUserLanguage]
  );

  const shouldShowNudgeOnTranscriptChange = useCallback(() => {
    const selectedTranscriptLanguageCode = shouldNudgeBeVisible.languageCode;
    if (!selectedTranscriptLanguageCode) return;
    const dubbedSubtitleKeys = getDubbedSubtitleKeys();
    if (dubbedSubtitleKeys.includes(selectedTranscriptLanguageCode)) {
      setNudgeLanguageCode(selectedTranscriptLanguageCode);
      setShouldShowNudge(true);
    } else {
      setShouldShowNudge(false);
      setNudgeLanguageCode(null);
      shouldShowNudgeOnMatchingUserLanguage();
      shouldShowNudgeOnSubtitleChange(selectedTranscriptLanguageCode);
    }
  }, [
    shouldNudgeBeVisible.languageCode,
    getDubbedSubtitleKeys,
    shouldShowNudgeOnMatchingUserLanguage,
    shouldShowNudgeOnSubtitleChange,
  ]);

  useEffect(() => {
    if (!isDubbingAvailable) return;
    shouldShowNudgeOnMatchingUserLanguage();
  }, [isDubbingAvailable, shouldShowNudgeOnMatchingUserLanguage]);

  useEffect(() => {
    // if the user switches to a transcript language that is available in dubbed audio, show the nudge
    if (!shouldNudgeBeVisible.isVisible || !isDubbingAvailable) return;
    shouldShowNudgeOnTranscriptChange();
    setShouldNudgeBeVisible({
      ...shouldNudgeBeVisible,
      isVisible: false,
    });
  }, [isDubbingAvailable, setShouldNudgeBeVisible, shouldNudgeBeVisible, shouldShowNudgeOnTranscriptChange]);

  return {
    setShouldNudgeBeVisible,
    shouldNudgeBeVisible,
    shouldShowNudge,
    setShouldShowNudge,
    nudgeLanguageCode,
    setNudgeLanguageCode,
    shouldShowNudgeOnSubtitleChange,
    shouldShowNudgeOnMatchingUserLanguage,
  };
};

export { useDubbingNudge };
